import React from "react"
import { Link, graphql } from "gatsby"
import styled from "@emotion/styled"
import tw from "twin.macro"
import Image from "gatsby-image"
import { ArrowRight } from "react-feather"

import {
  Layout,
  SEO,
  Carousel,
  Card,
  Button,
  LeftArrow,
  RightArrow,
  Product,
} from "../components"
import { getDiscountedPrice, IPrice } from "../helpers"
import { BlogIndexQuery } from "../../graphql-types"

const ForYouCategory = ({
  name,
  img,
  right = false,
}: {
  name: string
  img: any
  right?: boolean
}) => {
  return (
    <ForYouCategoryStyles.Wrapper>
      <ForYouCategoryStyles.Image
        fluid={img}
        alt={`${name} category`}
        right={right}
      />
      <ForYouCategoryStyles.Title>{name}</ForYouCategoryStyles.Title>
    </ForYouCategoryStyles.Wrapper>
  )
}

const ForYouCategoryStyles = {
  Wrapper: tw.div`flex flex-col items-center cursor-pointer`,
  Title: tw.h1`mt-2 text-base`,
  Image: styled(Image)<{ right: boolean }>`
    ${({ right }) =>
      right
        ? tw`w-24 h-24 md:w-32 md:h-32 lg:w-24 lg:h-24`
        : tw`w-48 h-56 md:w-64 md:h-64 lg:w-48 lg:h-56`}
  `,
}

interface Props {
  data: BlogIndexQuery
  location: Location
}

const MarketIndex: React.FC<Props> = ({ data, location }) => {
  const siteTitle = data.site.siteMetadata.title
  const discountedProducts = data.discountedProducts.edges
  const meatOfTheDay = data.meatOfTheDay.edges[0].node
  const fishOfTheDay = data.fishOfTheDay.edges[0].node

  return (
    <Layout location={location} title={siteTitle}>
      <SEO title="Online shopping of daily needs for your house" />

      <AnnouncementsCarousel
        slidesPerView={1}
        autoplay={{ delay: 5000 }}
        leftArrow={LeftArrow}
        rightArrow={RightArrow}
      >
        <FirstCarouselSlide.Wrapper>
          <FirstCarouselSlide.Container>
            <FirstCarouselSlide.Title>
              Last Opportunity
              <br />
              to Take Your Vegetables!
            </FirstCarouselSlide.Title>
            <FirstCarouselSlide.Subtitle>
              Find your favorite vegetables at
              <br />a discounted price
            </FirstCarouselSlide.Subtitle>
            <Link to="/deals/vegetable">
              <FirstCarouselSlide.Button aria-label="see more">
                See more
              </FirstCarouselSlide.Button>
            </Link>
          </FirstCarouselSlide.Container>

          <FirstCarouselSlide.ImageContainer>
            <FirstCarouselSlide.Image
              fluid={data.firstCarouselImage.childImageSharp.fluid}
              alt="Vegetables"
            />
          </FirstCarouselSlide.ImageContainer>
        </FirstCarouselSlide.Wrapper>
        <SecondCarouselSlide.Wrapper>
          <SecondCarouselSlide.Container>
            <SecondCarouselSlide.Title>
              We Always Think <br /> About You!
            </SecondCarouselSlide.Title>
            <SecondCarouselSlide.Subtitle>
              Bring more joy to your life
              <br /> with our new snacks layout
            </SecondCarouselSlide.Subtitle>
            <Link to="/categories/snacks">
              <SecondCarouselSlide.Button aria-label="see more">
                See more
              </SecondCarouselSlide.Button>
            </Link>
          </SecondCarouselSlide.Container>
          <SecondCarouselSlide.ImageContainer>
            <SecondCarouselSlide.Image
              fluid={data.secondCarouselImage.childImageSharp.fluid}
              alt="Vegetables"
            />
          </SecondCarouselSlide.ImageContainer>
        </SecondCarouselSlide.Wrapper>
      </AnnouncementsCarousel>

      <DailyDeals.Wrapper>
        <DailyDeals.Card>
          <DailyDeals.TextBox>
            <DailyDeals.Title>Daily Deals</DailyDeals.Title>
            <DailyDeals.Link to="/deals/">
              See all <ArrowRight />
            </DailyDeals.Link>
          </DailyDeals.TextBox>
          <DailyDeals.Carousel
            slidesPerView={1}
            centerSlides
            breakpoints={{
              798: {
                slidesPerView: 5,
              },
              680: {
                slidesPerView: 4,
              },
              426: {
                slidesPerView: 3,
                centerSlides: false,
              },
            }}
            leftArrow={LeftArrow}
            rightArrow={RightArrow}
          >
            {discountedProducts.map(
              ({
                node: {
                  fields: { slug },
                  frontmatter: { id, title, image, price, suffix },
                },
              }) => (
                <DailyDeals.CarouselItem key={id} to={`/products/${slug}`}>
                  <Product
                    title={title}
                    fluid={image.childImageSharp.fluid}
                    price={price as IPrice}
                    suffix={suffix}
                  />
                </DailyDeals.CarouselItem>
              )
            )}
          </DailyDeals.Carousel>
        </DailyDeals.Card>
      </DailyDeals.Wrapper>

      <Container>
        <ContainerCard.Wrapper>
          <ContainerCard.Title>For You</ContainerCard.Title>
          <ForYou.Categories>
            <ForYou.LeftCategoryWrapper to="/categories/meat">
              <ForYouCategory
                name="Meat"
                img={data.meatCategoryImage.childImageSharp.fluid}
              />
            </ForYou.LeftCategoryWrapper>
            <ForYou.RightCategories>
              <Link to="/categories/snack">
                <ForYouCategory
                  name="Snacks"
                  img={data.snackCategoryImage.childImageSharp.fluid}
                  right
                />
              </Link>
              <Link to="/categories/household">
                <ForYouCategory
                  name="Household"
                  img={data.householdCategoryImage.childImageSharp.fluid}
                  right
                />
              </Link>
            </ForYou.RightCategories>
          </ForYou.Categories>
        </ContainerCard.Wrapper>

        <ContainerCard.Wrapper>
          <ContainerCard.Title>Meat of the Day</ContainerCard.Title>
          <Link to={`/products/${meatOfTheDay.fields.slug}`}>
            <ContainerCard.Image
              fluid={meatOfTheDay.frontmatter.image.childImageSharp.fluid}
              alt={`${meatOfTheDay.frontmatter.title}`}
              imgStyle={{
                objectFit: "contain",
              }}
            />
            <ContainerCard.Info>
              <ContainerCard.Price>
                $
                {getDiscountedPrice(
                  meatOfTheDay.frontmatter.price.original,
                  meatOfTheDay.frontmatter.price.discount
                ).toFixed(2)}{" "}
                lb
                <ContainerCard.DiscountedPrice>
                  <OldPrice>
                    ${meatOfTheDay.frontmatter.price.original} lb
                  </OldPrice>
                  <span>·</span>
                  <span>
                    {(meatOfTheDay.frontmatter.price.discount * 100).toFixed(0)}
                    % off
                  </span>
                </ContainerCard.DiscountedPrice>
              </ContainerCard.Price>
              <ContainerCard.ProductTitle>
                {meatOfTheDay.frontmatter.title}
              </ContainerCard.ProductTitle>
            </ContainerCard.Info>
          </Link>
        </ContainerCard.Wrapper>

        <ContainerCard.Wrapper>
          <ContainerCard.Title>Fish of the Day</ContainerCard.Title>
          <Link to={`/products/${fishOfTheDay.fields.slug}`}>
            <ContainerCard.Image
              fluid={fishOfTheDay.frontmatter.image.childImageSharp.fluid}
              alt={`${fishOfTheDay.frontmatter.title}`}
              imgStyle={{
                objectFit: "contain",
              }}
            />
            <ContainerCard.Info>
              <ContainerCard.Price>
                $
                {getDiscountedPrice(
                  fishOfTheDay.frontmatter.price.original,
                  fishOfTheDay.frontmatter.price.discount
                ).toFixed(2)}{" "}
                lb
                <ContainerCard.DiscountedPrice>
                  <OldPrice>
                    ${fishOfTheDay.frontmatter.price.original} lb
                  </OldPrice>
                  <span>·</span>
                  <span>
                    {(fishOfTheDay.frontmatter.price.discount * 100).toFixed(0)}
                    % off
                  </span>
                </ContainerCard.DiscountedPrice>
              </ContainerCard.Price>
              <ContainerCard.ProductTitle>
                {fishOfTheDay.frontmatter.title}
              </ContainerCard.ProductTitle>
            </ContainerCard.Info>
          </Link>
        </ContainerCard.Wrapper>
      </Container>
    </Layout>
  )
}

const AnnouncementsCarousel = tw(
  Carousel
)`lg:max-w-screen-lg xl:max-w-screen-xl mx-auto w-full`

// First Slide Carousel Styles
const FirstCarouselSlide = {
  Wrapper: tw.div`relative flex flex-wrap px-16 pb-2 mx-auto`,
  Container: tw.div`z-20 flex flex-col py-4`,
  Title: tw.h1`mb-2 text-3xl font-semibold text-white`,
  Subtitle: tw.h2`mb-4 text-lg text-white`,
  Button: tw(
    Button
  )`px-8 py-2 self-start border-white text-white hover:text-black hover:bg-white`,
  ImageContainer: tw.div`absolute inset-0 z-0 flex-1 w-full before:content before:bg-black before:absolute before:w-full before:inset-0 before:z-10 before:opacity-50`,
  Image: tw(Image)`h-full`,
}

// Second Slide Carousel Styles
const SecondCarouselSlide = {
  Wrapper: tw.div`relative flex flex-wrap h-auto px-16 pb-2 mx-auto`,
  Container: tw.div`z-20 flex flex-col justify-around py-4`,
  Title: tw.h1`mb-2 text-3xl font-semibold text-white`,
  Subtitle: tw.h2`mb-4 text-lg text-white`,
  Button: tw(
    Button
  )`px-8 py-2 self-start border-white text-white hover:text-black hover:bg-white`,
  ImageContainer: tw.div`absolute inset-0 z-0 flex-1 w-full before:content before:bg-black before:absolute before:w-full before:inset-0 before:z-10 before:opacity-25`,
  Image: tw(Image)`h-full`,
}

// Daily Deals Styles
const DailyDeals = {
  Wrapper: tw.div`w-full px-2 mx-auto mt-6 xl:px-0 lg:max-w-screen-lg xl:max-w-screen-xl`,
  Card: tw(Card)`p-6 flex flex-col justify-center justify-around`,
  TextBox: tw.div`flex items-center mb-2`,
  Title: tw.h1`text-2xl text-black`,
  Link: styled(Link)`
    ${tw`flex items-center ml-2 text-sm`}

    & > svg {
      ${tw`transition-transform duration-200 ease-in-out`}
    }

    &:hover > svg {
      ${tw`transform translate-x-1`}
    }
  `,
  Carousel: tw(Carousel)`flex items-center justify-around`,
  CarouselItem: tw(Link)`flex justify-center`,
}

const OldPrice = tw.span`line-through`

// For You Styles
const ForYou = {
  Categories: tw.div`flex justify-around`,
  LeftCategoryWrapper: tw(Link)`self-center`,
  RightCategories: tw.div`flex flex-col justify-center`,
}

// Container styles
const Container = tw.div`flex flex-col-reverse justify-between w-full px-2 mx-auto mt-4 lg:mt-8 xl:px-0 lg:flex-row lg:max-w-screen-lg xl:max-w-screen-xl`

const ContainerCard = {
  Wrapper: styled(Card)`
    ${tw`flex-1 p-4`}

    &:not(:last-child) {
      ${tw`mt-4 lg:mt-0 lg:mr-4`}
    }
  `,
  Title: tw.h1`text-2xl text-black`,
  Image: tw(Image)`w-64 h-64 lg:w-56 lg:h-56 mx-auto`,
  Info: tw.div`flex items-center justify-between`,
  ProductTitle: tw.h2`text-lg font-semibold text-black`,
  Price: tw.div`flex flex-col text-base font-semibold`,
  DiscountedPrice: styled.span`
    ${tw`flex items-center text-sm font-semibold text-gray-500`}

    & > *:not(:last-child) {
      ${tw`mr-2`}
    }
  `,
}

export default MarketIndex

export const pageQuery = graphql`
  query BlogIndex {
    site {
      siteMetadata {
        title
      }
    }
    discountedProducts: allMarkdownRemark(
      limit: 10
      filter: { frontmatter: { price: { discount: { gt: 0 } } } }
    ) {
      edges {
        node {
          excerpt
          fields {
            slug
          }
          frontmatter {
            id
            title
            suffix
            price {
              original
              discount
            }
            image {
              childImageSharp {
                fluid {
                  ...GatsbyImageSharpFluid
                }
              }
            }
          }
        }
      }
    }
    firstCarouselImage: file(
      absolutePath: { regex: "/carousel-vegetables.jpg/" }
    ) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    secondCarouselImage: file(
      absolutePath: { regex: "/carousel-sweets.jpg/" }
    ) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    meatCategoryImage: file(absolutePath: { regex: "/meat-category.png/" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    snackCategoryImage: file(absolutePath: { regex: "/snack-category.png/" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    householdCategoryImage: file(
      absolutePath: { regex: "/household-category.png/" }
    ) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    meatOfTheDay: allMarkdownRemark(
      limit: 1
      filter: {
        frontmatter: {
          categories: { eq: "meat" }
          price: { discount: { gt: 0 } }
        }
      }
    ) {
      edges {
        node {
          fields {
            slug
          }
          frontmatter {
            id
            title
            price {
              original
              discount
            }
            image {
              childImageSharp {
                fluid {
                  ...GatsbyImageSharpFluid
                }
              }
            }
          }
        }
      }
    }
    fishOfTheDay: allMarkdownRemark(
      limit: 1
      filter: {
        frontmatter: {
          categories: { eq: "fish" }
          price: { discount: { gt: 0 } }
        }
      }
    ) {
      edges {
        node {
          fields {
            slug
          }
          frontmatter {
            id
            title
            price {
              original
              discount
            }
            image {
              childImageSharp {
                fluid {
                  ...GatsbyImageSharpFluid
                }
              }
            }
          }
        }
      }
    }
  }
`
